import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Typography,
  IconButton,
  Collapse,
  Box,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Button,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Tooltip
} from '@material-ui/core';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Map as MapIcon,
  FilterList,
  Search,
  LocationOn,
  LocalShipping,
  Schedule
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import { format, differenceInMinutes, startOfDay, endOfDay } from 'date-fns';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker, InfoWindow, Polyline } from '@react-google-maps/api';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  title: {
    flex: '1 1 100%',
  },
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 400px)',
  },
  groupRow: {
    backgroundColor: theme.palette.grey[50],
  },
  expandedRow: {
    backgroundColor: theme.palette.background.default,
  },
  stopDuration: {
    fontWeight: 'bold',
  },
  locationChip: {
    margin: theme.spacing(0.5),
  },
  yardChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  jobsiteChip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  otherChip: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.grey[900],
  },
  mapButton: {
    marginLeft: theme.spacing(1),
  },
  summaryCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  mapContainer: {
    height: '500px',
    width: '100%',
  },
  mapInfoWindow: {
    padding: theme.spacing(1),
  },
  stopMarkerLabel: {
    backgroundColor: 'white',
    padding: '2px 4px',
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: 'bold',
  }
}));

const GPSLocationHistoryViewer = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Filters
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [locationType, setLocationType] = useState('ALL');
  const [minStopDuration, setMinStopDuration] = useState(5); // minutes
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState('crew'); // 'crew' or 'equipment'
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Map Modal
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedGroupForMap, setSelectedGroupForMap] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(12);
  
  // Available options for filters
  const [availableCrews, setAvailableCrews] = useState([]);
  const [availableEquipment, setAvailableEquipment] = useState([]);

  // Fetch data
  const fetchLocationHistory = useCallback(async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_HOST
        ? process.env.REACT_APP_PROTOCOL + '://' + process.env.REACT_APP_HOST
        : window.location.origin;

      const response = await axios.get(`${baseUrl}/api/company/getGeoTabHistory`, {
        params: {
          startDate: startOfDay(startDate).toISOString(),
          endDate: endOfDay(endDate).toISOString(),
          crews: selectedCrews.join(','),
          equipment: selectedEquipment.join(','),
          locationType: locationType === 'ALL' ? undefined : locationType
        }
      });
     
      console.log('166....rawData', response)

      const rawData = response.data.data
      console.log(rawData)
      setData(rawData);
      
      // Extract unique crews and equipment for filters
      const crews = [...new Set(rawData.map(d => d.crewName).filter(Boolean))];
      const equipment = [...new Set(rawData.map(d => d.name).filter(Boolean))];
      setAvailableCrews(crews);
      setAvailableEquipment(equipment);
      
      // Process stops
      processStops(rawData);
    } catch (error) {
      console.error('Error fetching location history:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedCrews, selectedEquipment, locationType]);

  // Process raw data into stops
  const processStops = (rawData) => {
    const stops = [];
    const sortedData = [...rawData].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
    
    // Group by device/equipment
    const byDevice = {};
    sortedData.forEach(record => {
      const key = record.geotabDeviceId || record.device;
      if (!byDevice[key]) byDevice[key] = [];
      byDevice[key].push(record);
    });
    
    // Detect stops for each device
    Object.entries(byDevice).forEach(([deviceId, records]) => {
      let currentStop = null;
      
      records.forEach((record, index) => {
        const isMoving = record.speed > 2; // Consider > 2 mph as moving
        
        if (!isMoving) {
          if (!currentStop) {
            // Start new stop
            currentStop = {
              deviceId,
              crewName: record.crewName,
              equipmentName: record.name,
              startTime: new Date(record.date),
              endTime: new Date(record.date),
              lat: record.lat,
              lon: record.lon,
              locationType: record.locationType,
              matchedYardName: record.matchedYardName,
              matchedJobsiteName: record.matchedJobsiteName,
              records: [record]
            };
          } else {
            // Continue current stop
            currentStop.endTime = new Date(record.date);
            currentStop.records.push(record);
          }
        } else if (currentStop) {
          // End current stop
          const duration = differenceInMinutes(currentStop.endTime, currentStop.startTime);
          if (duration >= minStopDuration) {
            currentStop.duration = duration;
            stops.push(currentStop);
          }
          currentStop = null;
        }
      });
      
      // Handle last stop
      if (currentStop) {
        const duration = differenceInMinutes(currentStop.endTime, currentStop.startTime);
        if (duration >= minStopDuration) {
          currentStop.duration = duration;
          stops.push(currentStop);
        }
      }
    });
    
    // Apply search filter
    let filtered = stops;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = stops.filter(stop => 
        stop.crewName?.toLowerCase().includes(term) ||
        stop.equipmentName?.toLowerCase().includes(term) ||
        stop.matchedYardName?.toLowerCase().includes(term) ||
        stop.matchedJobsiteName?.toLowerCase().includes(term)
      );
    }
    
    setFilteredData(filtered);
    groupStops(filtered);
  };

  // Group stops by crew or equipment
  const groupStops = (stops) => {
    const groups = {};
    
    stops.forEach(stop => {
      const key = groupBy === 'crew' ? stop.crewName : stop.equipmentName;
      if (!key) return;
      
      if (!groups[key]) {
        groups[key] = {
          name: key,
          stops: [],
          totalDuration: 0,
          locationCounts: {}
        };
      }
      
      groups[key].stops.push(stop);
      groups[key].totalDuration += stop.duration;
      
      // Count location types
      const locKey = stop.matchedYardName || stop.matchedJobsiteName || 'Other';
      groups[key].locationCounts[locKey] = (groups[key].locationCounts[locKey] || 0) + 1;
    });
    
    setGroupedData(Object.values(groups));
  };

  // Handle row expansion
  const handleExpandClick = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Handle map view
  const handleMapView = (group) => {
    setSelectedGroupForMap(group);
    
    // Calculate center point from all stops
    if (group.stops.length > 0) {
      const bounds = group.stops.reduce((acc, stop) => {
        return {
          minLat: Math.min(acc.minLat, stop.lat),
          maxLat: Math.max(acc.maxLat, stop.lat),
          minLng: Math.min(acc.minLng, stop.lon),
          maxLng: Math.max(acc.maxLng, stop.lon),
        };
      }, {
        minLat: group.stops[0].lat,
        maxLat: group.stops[0].lat,
        minLng: group.stops[0].lon,
        maxLng: group.stops[0].lon,
      });
      
      setMapCenter({
        lat: (bounds.minLat + bounds.maxLat) / 2,
        lng: (bounds.minLng + bounds.maxLng) / 2,
      });
      
      // Adjust zoom based on bounds
      const latDiff = bounds.maxLat - bounds.minLat;
      const lngDiff = bounds.maxLng - bounds.minLng;
      const maxDiff = Math.max(latDiff, lngDiff);
      
      if (maxDiff < 0.01) setMapZoom(15);
      else if (maxDiff < 0.05) setMapZoom(13);
      else if (maxDiff < 0.1) setMapZoom(12);
      else if (maxDiff < 0.5) setMapZoom(10);
      else setMapZoom(9);
    }
    
    setMapOpen(true);
  };

  // Format duration
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Get location chip
  const getLocationChip = (stop) => {
    if (stop.matchedYardName) {
      return (
        <Chip
          label={stop.matchedYardName}
          size="small"
          className={`${classes.locationChip} ${classes.yardChip}`}
          icon={<LocationOn />}
        />
      );
    } else if (stop.matchedJobsiteName) {
      return (
        <Chip
          label={stop.matchedJobsiteName}
          size="small"
          className={`${classes.locationChip} ${classes.jobsiteChip}`}
          icon={<LocalShipping />}
        />
      );
    } else {
      return (
        <Chip
          label="Other Location"
          size="small"
          className={`${classes.locationChip} ${classes.otherChip}`}
        />
      );
    }
  };

  useEffect(() => {
    fetchLocationHistory();
  }, [fetchLocationHistory]);

  useEffect(() => {
    if (data && data.length) processStops(data);
  }, [data, minStopDuration, searchTerm, groupBy]);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Toolbar className={classes.toolbar}>
          <Typography className={classes.title} variant="h6" id="tableTitle">
            GPS Location History - Stop Analysis
          </Typography>
        </Toolbar>
        
        {/* Filters */}
        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Grid item xs={12} sm={6} md={2}>
                <KeyboardDatePicker
                  label="Start Date"
                  format="MM/dd/yyyy"
                  value={startDate}
                  onChange={setStartDate}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <KeyboardDatePicker
                  label="End Date"
                  format="MM/dd/yyyy"
                  value={endDate}
                  onChange={setEndDate}
                  fullWidth
                />
              </Grid>
            </MuiPickersUtilsProvider>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Location Type"
                value={locationType}
                onChange={(e) => setLocationType(e.target.value)}
                fullWidth
              >
                <MenuItem value="ALL">All Locations</MenuItem>
                <MenuItem value="YARD">Yards Only</MenuItem>
                <MenuItem value="JOBSITE">Jobsites Only</MenuItem>
                <MenuItem value="OTHER">Other Only</MenuItem>
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Min Stop Duration (min)"
                type="number"
                value={minStopDuration}
                onChange={(e) => setMinStopDuration(parseInt(e.target.value) || 5)}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={groupBy === 'equipment'}
                    onChange={(e) => setGroupBy(e.target.checked ? 'equipment' : 'crew')}
                  />
                }
                label="Group by Equipment"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search />
                }}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchLocationHistory}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Refresh Data'}
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        {/* Data Table */}
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{groupBy === 'crew' ? 'Crew' : 'Equipment'}</TableCell>
                <TableCell>Total Stops</TableCell>
                <TableCell>Total Stop Time</TableCell>
                <TableCell>Top Locations</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((group) => (
                  <React.Fragment key={group.name}>
                    <TableRow className={classes.groupRow}>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandClick(group.name)}
                        >
                          {expandedGroups[group.name] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1">{group.name}</Typography>
                      </TableCell>
                      <TableCell>{group.stops.length}</TableCell>
                      <TableCell className={classes.stopDuration}>
                        {formatDuration(group.totalDuration)}
                      </TableCell>
                      <TableCell>
                        {Object.entries(group.locationCounts)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 3)
                          .map(([loc, count]) => (
                            <Chip
                              key={loc}
                              label={`${loc} (${count})`}
                              size="small"
                              className={classes.locationChip}
                            />
                          ))}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MapIcon />}
                          onClick={() => handleMapView(group)}
                          className={classes.mapButton}
                        >
                          View Map
                        </Button>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedGroups[group.name]} timeout="auto" unmountOnExit>
                          <Box margin={1}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Time</TableCell>
                                  <TableCell>Duration</TableCell>
                                  <TableCell>Location</TableCell>
                                  <TableCell>Type</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.stops.map((stop, index) => (
                                  <TableRow key={index} className={classes.expandedRow}>
                                    <TableCell>
                                      <Box className={classes.summaryCell}>
                                        <Schedule fontSize="small" />
                                        {format(stop.startTime, 'h:mm a')} - {format(stop.endTime, 'h:mm a')}
                                      </Box>
                                    </TableCell>
                                    <TableCell>{formatDuration(stop.duration)}</TableCell>
                                    <TableCell>
                                      {stop.matchedYardName || stop.matchedJobsiteName || 'Unknown'}
                                    </TableCell>
                                    <TableCell>{getLocationChip(stop)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={groupedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={(event, newPage) => setPage(newPage)}
          onChangeRowsPerPage={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
      
      {/* Map Modal */}
      <Dialog
        open={mapOpen}
        onClose={() => {
          setMapOpen(false);
          setSelectedMarker(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedGroupForMap?.name} - Stop Locations ({selectedGroupForMap?.stops.length} stops)
        </DialogTitle>
        <DialogContent>
          <Box className={classes.mapContainer}>
            <LoadScript googleMapsApiKey={process.env.REACT_APP_GMAPKEY}>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapCenter}
                zoom={mapZoom}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
                {/* Stop Markers */}
                {selectedGroupForMap?.stops.map((stop, index) => {
                  const markerIcon = {
                    url: stop.locationType === 'YARD' 
                      ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                      : stop.locationType === 'JOBSITE'
                      ? 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                      : 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                    ...(window.google?.maps && {
                      scaledSize: new window.google.maps.Size(32, 32),
                    }),
                  };
                  
                  // const markerIcon = {
                  //   url: stop.locationType === 'YARD' 
                  //     ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
                  //     : stop.locationType === 'JOBSITE'
                  //     ? 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                  //     : 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  //   scaledSize: new window.google.maps.Size(32, 32),
                  // };
                  
                  return (
                    <Marker
                      key={index}
                      position={{ lat: stop.lat, lng: stop.lon }}
                      icon={markerIcon}
                      onClick={() => setSelectedMarker(stop)}
                      label={{
                        text: `${index + 1}`,
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    />
                  );
                })}
                
                {/* Info Window */}
                {selectedMarker && (
                  <InfoWindow
                    position={{ lat: selectedMarker.lat, lng: selectedMarker.lon }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <Box className={classes.mapInfoWindow}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Stop Details</strong>
                      </Typography>
                      <Typography variant="body2">
                        <strong>Time:</strong> {format(selectedMarker.startTime, 'h:mm a')} - {format(selectedMarker.endTime, 'h:mm a')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Duration:</strong> {formatDuration(selectedMarker.duration)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Location:</strong> {selectedMarker.matchedYardName || selectedMarker.matchedJobsiteName || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {selectedMarker.locationType}
                      </Typography>
                    </Box>
                  </InfoWindow>
                )}
                
                {/* Optional: Draw route lines between stops */}
                {selectedGroupForMap?.stops.length > 1 && (
                  <Polyline
                    path={selectedGroupForMap.stops.map(stop => ({ lat: stop.lat, lng: stop.lon }))}
                    options={{
                      strokeColor: '#FF0000',
                      strokeOpacity: 0.5,
                      strokeWeight: 2,
                    }}
                  />
                )}
              </GoogleMap>
            </LoadScript>
          </Box>
          
          {/* Stop Legend */}
          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <Chip
              icon={<LocationOn />}
              label="Yard"
              size="small"
              className={classes.yardChip}
            />
            <Chip
              icon={<LocalShipping />}
              label="Jobsite"
              size="small"
              className={classes.jobsiteChip}
            />
            <Chip
              label="Other"
              size="small"
              className={classes.otherChip}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GPSLocationHistoryViewer;